import { api } from "./client";

// Client-side surface of the practitioner layer. Everything here is
// scoped server-side to the authenticated user — and nothing in these
// payloads can carry journal text (API contract, tested server-side).
export const practitionerLinkApi = {
  // Own links, with the practitioner's display name for the consent copy.
  myLinks: (token) => api.get("/practitioners/links", token),

  // Accept or decline a practitioner link. Declining pauses anything
  // the practitioner had staged — the server enforces that consent is
  // a live requirement, not a one-time checkbox.
  respond: (linkId, accept, token) =>
    api.post(`/practitioners/links/${linkId}/consent`, { accept }, token),

  // Own assigned protocols (behaviors joined with library names).
  assigned: (token) => api.get("/practitioner-protocols/assigned", token),

  // Adherence for one of the user's own protocols.
  adherence: (protocolId, token) =>
    api.get(`/practitioner-adherence/${protocolId}`, token),

  // Mark a behavior done (or undone) for a local calendar day.
  logAdherence: (payload, token) =>
    api.post("/practitioner-adherence", payload, token),

  // ── Per-entry journal sharing ─────────────────────────────────────
  // The one opt-in channel through the journal block. Nothing here is
  // implicit: an entry reaches a practitioner only after shareEntries
  // names it, and revokeEntry takes it back on their next read.

  // The user's own recent reflections + whether each is already shared.
  shareableEntries: (linkId, token, days = 30) =>
    api.get(`/practitioners/links/${linkId}/shareable-entries?days=${days}`, token),

  // "This is what your practitioner can see" — the client's receipt.
  sharedEntries: (linkId, token) =>
    api.get(`/practitioners/links/${linkId}/shared-entries`, token),

  // shareMode: 'summary' (one-sentence takeaway) | 'excerpt' (the entry).
  //
  // Written entries come from two tables — daily_checkins and the
  // free-write journal_entries — and the ids go in different fields
  // because the approval row's CHECK constraint requires exactly one
  // subject per row. `source` is the discriminator the candidate list
  // hands back; it defaults to 'checkin' so pre-journal callers that
  // pass a bare id array are unaffected.
  shareEntries: (linkId, entryIds, shareMode, token, source = "checkin") =>
    api.post(`/practitioners/links/${linkId}/shared-entries`,
      source === "journal"
        ? { journal_entry_ids: entryIds, share_mode: shareMode }
        : { checkin_ids: entryIds, share_mode: shareMode },
      token),

  // Takes either kind of id: the two id spaces are disjoint, so the
  // server resolves the approval without being told which it is.
  // Revocation staying callable with nothing but the id is deliberate —
  // it's the direction that reduces disclosure.
  revokeEntry: (linkId, entryId, token) =>
    api.delete(`/practitioners/links/${linkId}/shared-entries/${entryId}`, token),

  revokeAllEntries: (linkId, token) =>
    api.delete(`/practitioners/links/${linkId}/shared-entries`, token),

  // ── Flow 1: invite by email/phone ───────────────────────────────

  // PUBLIC — no token. The pre-login identity disclosure on the
  // /invite/:token landing page: who's asking, before any auth happens.
  getInvitePublic: (inviteToken) =>
    api.get(`/practitioners/invites/${inviteToken}`),

  // Claim the invite for the signed-in user. Creates the link
  // (consent_given still false) and returns it — the same shape as
  // myLinks(), so the caller can hand it straight to ConsentDialog.
  redeemInvite: (inviteToken, token) =>
    api.post(`/practitioners/invites/${inviteToken}/redeem`, {}, token),
};
